<template>
  <b-card
    v-if="data"
    no-body
    class="card-transaction order-statistics-card"
  >
    <b-card-header class="order-statistics-header">
      <h4 class="chart-title mb-0">
        Order Statistics
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="tobe-widget-body">
      <b-row class="tobe-widget-grid">
        <b-col
          v-for="item in data"
          :key="item.icon"
          md="6"
          lg="4"
          xl="4"
          class="tobe-widget-col stat-col order-stat-col"
        >
          <div
            class="stat-box"
            :class="`stat-box-${item.color}`"
          >
            <div class="stat-box-content flex">
              <div class="stat-box-left">
                <div class="stat-value-spacer" />
                <h4
                  v-b-tooltip.hover.v-primary
                  class="stat-value"
                  :title="item.subtitle === 'Orders Count' ? item.title.toLocaleString() : __numberWithCommas(item.title, true)"
                >
                  {{ formatValue(item.title, item.subtitle) }}
                </h4>
                <p class="stat-label">
                  {{ item.subtitle }}
                </p>
              </div>
              <div class="stat-box-right">
                <div
                  class="stat-icon-wrapper"
                  :class="`stat-icon-${item.color}`"
                >
                  <feather-icon
                    size="18"
                    :icon="item.icon"
                    class="stat-icon"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  VBTooltip,
} from 'bootstrap-vue'
import { kFormatter } from '@core/utils/filter'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.orderstatistics
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    kFormatter,
    formatValue(value, subtitle) {
      if (value === null || value === undefined) {
        if (subtitle === 'Orders Count') return '0'
        if (subtitle === 'Delivery Charge') return '0.00'
        return '0.0'
      }
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (Number.isNaN(numValue)) {
        if (subtitle === 'Orders Count') return '0'
        if (subtitle === 'Delivery Charge') return '0.00'
        return '0.0'
      }
      // Use kFormatter for all values to display in k format (1k, 2k, etc.)
      return this.kFormatter(numValue)
    },
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.order-statistics-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  transition: all 0.3s ease;
  background: #fff;
}

.tobe-widget-body {
  padding: 1.5rem;
}

.tobe-widget-col {
  padding: 0.5rem;
  display: flex;
}

.order-statistics-card:hover {
  box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
  transform: translateY(-2px);
}

.order-statistics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem 0.75rem 1.5rem;
  background: #fff;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.statistics-body {
  padding: 0;
}

.match-height .stat-col {
  display: flex;
  flex-flow: column;
}

.match-height .stat-col > .stat-box {
  flex: 1 1 auto;
}

.stat-col {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.order-stat-col {
  flex: 0 0 auto;
  width: auto;
  max-width: none;
}

.financial-statistics-row {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
}

.stat-box {
  border-radius: 1rem;
  padding: 1.5rem;
  height: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  flex: 0 0 auto;
  width: 320px;
  min-width: 320px;
  max-width: 320px;
}

.stat-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
}

.stat-box-light-info {
  background: #e3f2fd;
}

.stat-box-light-success {
  background: #e8f5e9;
}

.stat-box-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  position: relative;
  z-index: 1;
  height: 100%;
}

.stat-box-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  position: relative;
}

.stat-value-spacer {
  flex: 1;
  min-height: 0;
}

.stat-value {
  font-size: 1.625rem;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 0.5rem;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-family: 'Roboto', sans-serif;
  align-self: flex-start;
}

.stat-label {
  font-size: 1.125rem;
  color: #6e6b7b;
  margin-bottom: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  font-weight: 500;
  line-height: 1.4;
  font-family: 'Roboto', sans-serif;
  align-self: flex-start;
}

.stat-box-right {
  margin-left: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  align-self: flex-start;
}

.stat-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.stat-icon-light-info {
  background-color: #bbdefb;
}

.stat-icon-light-success {
  background-color: #c8e6c9;
}

.stat-icon {
  color: #5e5873;
  position: relative;
  z-index: 1;
}

.stat-icon-light-info .stat-icon {
  color: #1565c0;
}

.stat-icon-light-success .stat-icon {
  color: #2e7d32;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .timestamp-badge {
    margin-top: 0.5rem;
  }

  .stat-box {
    min-height: 110px;
    padding: 1.25rem;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .stat-icon-wrapper {
    width: 48px;
    height: 48px;
  }
}
</style>
